import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconHome,
  IconHomeFilled,
  IconFolder,
  IconFolderFilled,
  IconUsersGroup,
  IconCalendarTime,
} from "@tabler/icons-react";
import styles from "./MNavigationBar.module.css";
import { useTranslation } from "react-i18next";
import MBadge from "./MBadge";
import { useNotesContext } from "@/context/NotesContext";
import { clearNavigationSource } from "@/utils/navigationSource";

export type NavigationItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  path: string;
};

export interface MNavigationBarProps {
  items?: NavigationItem[];
  defaultItems?: boolean;
  onItemClick?: (item: NavigationItem) => void;
  storageKey?: string;
}

/**
 * MFooterNavigation component for mobile navigation
 *
 * @param {MNavigationBarProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const MFooterNavigation: React.FC<MNavigationBarProps> = ({
  items,
  defaultItems = true,
  onItemClick,
  storageKey = "maatra-active-nav-item",
}) => {
  const { unreadCount } = useNotesContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const defaultNavigationItems: NavigationItem[] = [
    {
      id: "home",
      label: t("navigation.home"),
      icon: <IconHome size={28} stroke={1.5} />,
      activeIcon: <IconHomeFilled size={28} />,
      path: "/",
    },
    {
      id: "documents",
      label: t("navigation.documents"),
      icon: <IconFolder size={28} stroke={1.5} />,
      activeIcon: <IconFolderFilled size={28} />,
      path: "/documents",
    },
    {
      id: "family",
      label: t("navigation.family"),
      icon: <IconUsersGroup size={28} stroke={1.5} />,
      activeIcon: <IconUsersGroup size={28} />,
      path: "/profile/family",
    },
    {
      id: "reminders",
      label: t("navigation.reminders"),
      icon: <IconCalendarTime size={28} stroke={1.5} />,
      activeIcon: <IconCalendarTime size={28} stroke={2} />,
      path: "/reminders",
    },
  ];

  const navigationItems = items || (defaultItems ? defaultNavigationItems : []);

  // Get active item from local storage or determine from current path
  const getInitialActiveItem = (): string => {
    const storedActiveItem = localStorage.getItem(storageKey);

    if (storedActiveItem) {
      return storedActiveItem;
    }

    // Find matching item based on current path
    const currentPath = location.pathname;
    const matchingItem = navigationItems.find(
      (item) =>
        currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );

    return matchingItem?.id || navigationItems[0]?.id || "";
  };

  const [activeItemId, setActiveItemId] =
    useState<string>(getInitialActiveItem);

  // Update active item when location changes
  useEffect(() => {
    const currentPath = location.pathname;

    // Don't highlight any icon when on non-family profile pages (basic-information, health-profile, emergency-card, etc.)
    // But DO highlight family icon when on family pages
    const isNonFamilyProfilePage =
      currentPath.startsWith("/profile") &&
      !currentPath.startsWith("/profile/family") &&
      (currentPath.includes("/basic-information") ||
        currentPath.includes("/health-profile") ||
        currentPath.includes("/emergency-card"));

    if (isNonFamilyProfilePage) {
      setActiveItemId("");
      return;
    }

    if (currentPath.startsWith("/lab-insights")) {
      setActiveItemId("documents");
      return;
    }

    const matchingItem = navigationItems.find(
      (item) =>
        currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    );

    if (matchingItem && matchingItem.id !== activeItemId) {
      setActiveItemId(matchingItem.id);
      // Commented Out for now
      //localStorage.setItem(storageKey, matchingItem.id);
    }
  }, [location.pathname, navigationItems, activeItemId, storageKey]);

  const handleItemClick = (item: NavigationItem) => {
    setActiveItemId(item.id);
    // Commented Out for now
    //localStorage.setItem(storageKey, item.id);

    // Clear navigation source when footer navigation is clicked
    clearNavigationSource();

    if (onItemClick) {
      onItemClick(item);
    } else {
      navigate(item.path);
    }
  };

  return (
    <nav className={styles.navigationBar} data-footer-nav>
      {navigationItems.map((item, index) => {
        const isActive = activeItemId === item.id;
        const isFamily = item.id === "family";
        const isHome = item.id === "home";
        const isRecords = item.id === "documents";

        return (
          <div
            key={item.id}
            className={`${styles.navItem} ${isActive ? styles.active : ""}`}
            onClick={() => handleItemClick(item)}
            role="button"
            tabIndex={0}
            aria-label={item.label}
            aria-current={isActive ? "page" : undefined}
            data-footer-home={isHome ? "" : undefined}
            data-footer-records={isRecords ? "" : undefined}
            data-footer-family={isFamily ? "" : undefined}
          >
            {isActive && (
              <div
                className={`${styles.indicator} ${
                  styles[`indicator-position-${index}`]
                }`}
              />
            )}
            <div
              className={styles.navItemIcon}
              style={{ position: "relative" }}
            >
              {isActive ? item.activeIcon : item.icon}
              {isFamily && unreadCount > 0 && (
                <MBadge
                  variant="indigo-outline"
                  className="footerBadgeAbsolute"
                >
                  {unreadCount}
                </MBadge>
              )}
            </div>
            <span className={styles.navItemLabel}>{item.label}</span>
          </div>
        );
      })}
    </nav>
  );
};

export default MFooterNavigation;
